import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { MsalService } from "@azure/msal-angular";
import { map, BehaviorSubject, Observable, tap, catchError, throwError, switchMap, of } from "rxjs";
import { environment } from "../../environments/environment";

interface GraphUser {
  displayName: string;
  userPrincipalName: string;
  mail: string;
  isAdmin: boolean;
  isOwner: boolean;
}

interface GraphResponse {
  value: GraphUser[];
}

export interface DirectoryUser {
  id: string;
  displayName: string | null;
  mail: string | null;
  userPrincipalName: string | null;
  onPremisesSamAccountName: string | null;
  accountEnabled: boolean | null;
}

@Injectable({
  providedIn: 'root'
})

export class UserService {

  url = environment.backend_base_url; // Base URL for the API
  private userSubject = new BehaviorSubject<any>(null); // Initialize with null or default value
  public user$ = this.userSubject.asObservable();

  constructor(public _http: HttpClient, private msalService: MsalService) { }

  /**
   * Sets the User Observable to the data passed in
   * @param data JSON object that is information about user, retrieved from database.
   */
  setUserObservable(data: any) {
    this.userSubject.next(data);
  }

  /**
   * Call to MSFT graph to get signed in users data
   * @returns JSON object that is information about user
   */
  graphMeCall() {
    const meEndpoint = 'https://graph.microsoft.com/v1.0/me';

    return this._http.get<any>(meEndpoint).pipe(
      switchMap((me: any) => {
        const userPrincipalName = me?.userPrincipalName;
        if (!userPrincipalName) {
          return of({
            ...me,
            picture: null,
            pictureBase64: null
          });
        }

        const graphPhotoEndpoint = `https://graph.microsoft.com/v1.0/users/${userPrincipalName}/photo/$value`;
        return this._http.get(graphPhotoEndpoint, { responseType: 'blob' }).pipe(
          switchMap((blob: Blob) => this.blobToBase64(blob)),
          map((base64Photo: string | null) => {
            const rawBase64 = base64Photo ? base64Photo.split(',')[1] || base64Photo : null;
            return {
              ...me,
              picture: base64Photo,
              pictureBase64: rawBase64
            };
          }),
          catchError(() => of({
            ...me,
            picture: null,
            pictureBase64: null
          }))
        );
      })
    );
  }

  /**
   * Gets a user photo payload by userPrincipalName from Microsoft Graph
   * @param userPrincipalName User principal name (UPN)
   * @returns Object containing UPN and photo data in base64 formats
   */
  getUserByGmid(userPrincipalName: string) {
    if (!userPrincipalName) {
      return of({
        userPrincipalName: null,
        picture: null,
        pictureBase64: null
      });
    }

    const graphPhotoEndpoint = `https://graph.microsoft.com/v1.0/users/${userPrincipalName}/photo/$value`;

    return this._http.get(graphPhotoEndpoint, { responseType: 'blob' }).pipe(
      switchMap((blob: Blob) => this.blobToBase64(blob)),
      map((base64Photo: string | null) => {
        const rawBase64 = base64Photo ? base64Photo.split(',')[1] || base64Photo : null;
        return {
          userPrincipalName,
          picture: base64Photo,
          pictureBase64: rawBase64
        };
      }),
      catchError(() => of({
        userPrincipalName,
        picture: null,
        pictureBase64: null
      }))
    )
  }

  getUserByGMID(userPrincipalName: string) {
    return this.getUserByGmid(userPrincipalName);
  }

  private blobToBase64(blob: Blob): Observable<string | null> {
    return new Observable<string | null>((observer) => {
      const reader = new FileReader();

      reader.onloadend = () => {
        observer.next((reader.result as string) || null);
        observer.complete();
      };

      reader.onerror = () => {
        observer.next(null);
        observer.complete();
      };

      reader.readAsDataURL(blob);
    });
  }

  /**
   * Get user's profile photo from Microsoft Graph
   * @returns Observable with the profile photo blob URL
   */
  getMyGraphProfilePhoto(): Observable<string | null> {
    const meEndpoint = 'https://graph.microsoft.com/v1.0/me';
    return this._http.get<any>(meEndpoint).pipe(
      switchMap((me: any) => this.getUserByGmid(me?.userPrincipalName)),
      map((photoPayload: any) => photoPayload?.picture || null),
      catchError(() => of(null))
    );
  }

  getOtherGraphProfilePhoto(userPrincipalName: string): Observable<any> {
    console.log("Get other graph profile", userPrincipalName);
    const graphEndpoint = `https://graph.microsoft.com/v1.0/users/${userPrincipalName}/photo/$value`;
    return this._http.get(graphEndpoint).pipe(
      map((response) => response)
    );
  }
  
  /**
   * Search for an event leader using Microsoft Graph API
   * @param person The name, email, or identifier of the person to search for
   */
  searchPerson(person: string): Observable<GraphUser[]> {
    const encodedPersonInput = encodeURIComponent(person);
    const graphEndpoint = `https://graph.microsoft.com/v1.0/users?$filter=startswith(displayName,'${encodedPersonInput}') or startswith(mail,'${encodedPersonInput}') or startswith(userPrincipalName,'${encodedPersonInput}') or startswith(employeeId,'${encodedPersonInput}') or displayName eq '${encodedPersonInput}' or mail eq '${encodedPersonInput}' or userPrincipalName eq '${encodedPersonInput}' or employeeId eq '${encodedPersonInput}'`;
  
    return this._http.get<GraphResponse>(graphEndpoint).pipe(
      map((response) => response.value || [])
    );
  }

  /**
   * Search for a person using Microsoft Graph API
   * @param searchValue The name to search for
   * @returns An observable containing the search results
   */
  searchForPersonByName(searchValue: string) {
    const encodedSearchValue = encodeURIComponent(searchValue);
    const graphEndpoint = `https://graph.microsoft.com/v1.0/users?&$search="displayName:${encodedSearchValue}"` + "&$select=mail,onPremisesSamAccountName,displayName,jobTitle,userPrincipalName,officeLocation,department,accountEnabled," +"extension_ef42828ce93541bd877d75958d40e00f_gmGMINanchor," +"extension_ef42828ce93541bd877d75958d40e00f_employeeType,employeeId";
    const headers = {
      'ConsistencyLevel': 'eventual' // search can still proceed even if data isn't synced across all replicas
    }
  
    return this._http.get<GraphResponse>(graphEndpoint, { headers }).pipe(
      map((response) => {
        console.log('Graph API Response:', response.value);
        if (response.value && response.value.length > 0) {
        // Loop through each user and modify as needed
        response.value.forEach((user: any) => {
          // Lowercase onPremisesSamAccountName if it exists
          if (user.onPremisesSamAccountName) {
            user.onPremisesSamAccountName = user.onPremisesSamAccountName.toLowerCase();
          }
        });
        return response.value; // Return the list of users
        } else {
          return null; // No matching user found
        }
      })
    );
  }

  /**
   * Exact-match directory lookup by GMID (samAccountName) or email/UPN.
   */
  findDirectoryUsers(identifier: string): Observable<DirectoryUser[]> {
    // OData string literals escape single quotes by doubling them.
    const value = identifier.trim().toLowerCase().replace(/'/g, "''");
    const filter = value.includes('@')
      ? `mail eq '${value}' or userPrincipalName eq '${value}'`
      : `onPremisesSamAccountName eq '${value}'`;
    const params = new HttpParams()
      .set('$filter', filter)
      .set('$select', 'id,displayName,mail,userPrincipalName,onPremisesSamAccountName,accountEnabled')
      .set('$count', 'true');
    // onPremisesSamAccountName filtering is an advanced query in Graph.
    const headers = new HttpHeaders({ ConsistencyLevel: 'eventual' });

    return this._http.get<{ value: DirectoryUser[] }>('https://graph.microsoft.com/v1.0/users', { params, headers }).pipe(
      map(response => response.value || [])
    );
  }

  getDirectoryUserById(id: string): Observable<DirectoryUser> {
    const params = new HttpParams()
      .set('$select', 'id,displayName,mail,userPrincipalName,onPremisesSamAccountName,accountEnabled');
    return this._http.get<DirectoryUser>(`https://graph.microsoft.com/v1.0/users/${encodeURIComponent(id)}`, { params });
  }

  /**
   * Initialize MSAL instance
   * This method is called to ensure MSAL is ready for use.
   */
  async initializeMsal() {
    try {
      await this.msalService.instance.initialize();
    } catch (error) {
      console.error('MSAL initialization failed:', error);
    }
  }

  /**
   * Get current user from the observable
   */
  getCurrentUser() {
    return this.userSubject.value;
  }

  /**
   * Refresh current user's viewer and admin permissions
   * This checks the database for the latest permissions
   */
  refreshCurrentUserPermissions() {
    const currentUser = this.getCurrentUser();
    if (!currentUser || !currentUser.gmid) {
      console.log('No current user to refresh permissions for');
      return;
    }

    console.log('🔄 Refreshing permissions for user:', currentUser.gmid);

    // Check admin status
    this._http.get(`${this.url}/api/admin/find/${currentUser.gmid}`).subscribe({
      next: (adminResult: any) => {
        if (adminResult && adminResult.admin) {
          currentUser.isAdmin = true;
          currentUser.isOwner = adminResult.admin.owner || false;
        } else {
          currentUser.isAdmin = false;
          currentUser.isOwner = false;
        }

        // Check viewer status
        this._http.get(`${this.url}/api/viewer/all`).subscribe({
          next: (viewerResult: any) => {
            if (viewerResult && viewerResult.find((v: any) => v.gmid.toLowerCase() === currentUser.gmid.toLowerCase())) {
              currentUser.isViewer = true;
              console.log('✅ User has viewer permissions');
            } else {
              currentUser.isViewer = false;
              console.log('❌ User does not have viewer permissions');
            }

            // Update the user observable with refreshed permissions
            this.setUserObservable(currentUser);
          },
          error: (error) => {
            console.error('Error checking viewer status:', error);
            currentUser.isViewer = false;
            this.setUserObservable(currentUser);
          }
        });
      },
      error: (error) => {
        console.error('Error checking admin status:', error);
        currentUser.isAdmin = false;
        currentUser.isOwner = false;
        
        // Still check viewer status even if admin check fails
        this._http.get(`${this.url}/api/viewer/all`).subscribe({
          next: (viewerResult: any) => {
            if (viewerResult && viewerResult.find((v: any) => v.gmid.toLowerCase() === currentUser.gmid.toLowerCase())) {
              currentUser.isViewer = true;
            } else {
              currentUser.isViewer = false;
            }
            this.setUserObservable(currentUser);
          },
          error: () => {
            currentUser.isViewer = false;
            this.setUserObservable(currentUser);
          }
        });
      }
    });
  }

}
